import type L from 'leaflet';
import {
  TRIANGLE_PATH,
  HOUSE_PATH,
  BORDER_RADIUS,
  TYPOGRAPHY,
  ON_COLOR,
  MAP_CLUSTER_FILL,
  PROPERTY_CLUSTER_FILL,
} from '../../theme';
import { clusterBubbleSize, propertyClusterSize } from './markerSizes';
import type { MapStyle } from '../../store/uiStore';

// Self-hosted (copied from node_modules/leaflet/dist into public/leaflet —
// re-copy on Leaflet upgrades). Same-origin avoids the unpkg.com DNS/TLS
// round trip at map init and lets the service worker cache it.
export const LEAFLET_CSS = '/leaflet/leaflet.css';

// Minimum Leaflet zoom at which we show per-structure address labels.
export const LABEL_MIN_ZOOM = 17;

// Zoom at and above which service-area polygon outlines are hidden to avoid
// competing visually with address-level structure labels.
export const LABEL_HIDE_POLYGON_ZOOM = 17;

export const STRUCTURE_LABEL_CSS = `
  .structure-label {
    background: rgba(255,255,255,0.9);
    border: none;
    box-shadow: 0 1px 2px rgba(0,0,0,0.2);
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 600;
    color: #111;
    border-radius: 4px;
  }
  .structure-label::before { display: none; }
  .leaflet-container.hide-structure-labels .structure-label { display: none; }
  .sc-cluster {
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${PROPERTY_CLUSTER_FILL}40;
    border-radius: 50%;
  }
  .sc-cluster div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  /* Observation clusters. Circle, because a circle is what an observation is
     (see the marker grammar) — the same shape, standing for several. The slate
     below is the fallback for a cluster with no dominant category; one that is
     mostly a single category gets that category's color inlined by
     makeObservationClusterIcon, so the map keeps its color when zoomed out.
     Either way it stays distinct from .sc-cluster, a slate house that stands for a
     pile of properties. */
  .obs-cluster {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .obs-halo {
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${MAP_CLUSTER_FILL}38;
    border-radius: 50%;
  }
  .obs-core {
    background: ${MAP_CLUSTER_FILL}EB;
    border: 2px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  }
  /* On phones the floating tab bar pill spans nearly the full width and
     covers the bottom-right corner — lift the OSM attribution above it so
     the credit stays readable (required by the OSM license). Wider screens
     keep the default corner placement (the centered pill leaves it clear). */
  @media (max-width: 520px) {
    .leaflet-control-attribution {
      margin-bottom: 76px !important;
    }
  }
`;

// Container class toggled by useDefensePlanDraw.web.ts while a draw tool is
// selected. CSS, not per-handler refs, does the click suppression: one rule
// covers clusters, structures, posts, hover-card tooltips and the plan's own
// lines, so a click while drawing always reaches the map, never a marker
// underneath it.
export const DEFENSE_PLAN_DRAWING_CLASS = 'drawing-defense-plan';
// Added alongside it while the Home kind is armed: the house pins are the
// targets then, so PLAN_FOCUS_CSS's 35% fade would hide what people click.
export const DEFENSE_PLAN_HOME_CLASS = 'drawing-defense-plan-home';
/** The length/area tag that follows the cursor while a line or area is drawn. */
export const DEFENSE_PLAN_READOUT_CLASS = 'redi-plan-readout';
/** Put on a vertex dot for one shake when a finish gesture has too few points. */
export const DEFENSE_PLAN_VERTEX_SHAKE_CLASS = 'redi-vertex-shake';

// Leaflet's createPane('defensePlan') names the pane's element
// `leaflet-defensePlan-pane` (DomUtil.create('div', 'leaflet-pane
// leaflet-<name>-pane', ...), the same convention fhszPane already uses as
// `.leaflet-fhszPane-pane`). Kept as a literal, not imported from
// useDefensePlanLayer.web.ts, so this file never needs to know about the
// hooks that own the pane.
// Toggled on a beacon marker's own element (useDefensePlanLayer.web.ts) when
// its feature is the selected one, same event the shape layers highlight on
// — see setHighlight there. The scale lives on the icon's inner ring div,
// not the outer `.leaflet-marker-icon` element: Leaflet already puts a
// `transform: translate3d(...)` on that element to position the marker, and
// a second `transform` on the same element would replace it, not compose,
// silently teleporting the beacon.
export const DEFENSE_PLAN_BEACON_SELECTED_CLASS = 'leaflet-defense-plan-beacon-selected';
// Lighter than selected: the pointer is over the feature's row or shape.
export const DEFENSE_PLAN_BEACON_HOVERED_CLASS = 'leaflet-defense-plan-beacon-hovered';

// While a draw tool is armed, every map click must reach the draw hook. The
// pane rule alone isn't enough: Leaflet's own CSS gives each interactive
// marker and vector `pointer-events: auto`, which beats a parent's `none`.
// Before Sept 16 2026 a click on a house pin opened that property and closed
// the plan editor, and Home mode asks people to click exactly there.
//
// Every pane that can hold an interactive layer belongs in this list, not
// only the ones that did when it was written. Leaflet names a pane's class by
// dropping the first "Pane" from the pane name, so `planMapsPane` is
// `.leaflet-planMaps-pane`. The names themselves live with the layers that
// create the panes — useMapOverlaysLayer.web.ts (planMapsPane) and
// communityPrograms.ts (firewisePane, fireSafeCouncilsPane) — and are spelled
// out here rather than imported, since both of those import this file.
// The community program panes were missed when e74bd71 (Sept 22 2026) made
// their cluster markers clickable, which reopened the Sept 16 fault on a
// newer layer: with Firewise on, a click near a pin zoomed the map instead of
// placing a vertex.
export const DEFENSE_PLAN_CSS = `
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} {
    cursor: crosshair;
  }
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-marker-pane,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-tooltip-pane,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-defensePlan-pane,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-marker-pane .leaflet-interactive,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-defensePlan-pane .leaflet-interactive,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-planMaps-pane,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-planMaps-pane *,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-firewise-pane,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-firewise-pane *,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-fireSafeCouncils-pane,
  .leaflet-container.${DEFENSE_PLAN_DRAWING_CLASS} .leaflet-fireSafeCouncils-pane * {
    pointer-events: none !important;
  }
  .${DEFENSE_PLAN_READOUT_CLASS} {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 800;
    pointer-events: none;
    display: none;
    padding: 1px 6px;
    border-radius: ${BORDER_RADIUS.xs}px;
    border: 1px solid transparent;
    white-space: nowrap;
    font-family: ${TYPOGRAPHY.caption.fontFamily}, sans-serif;
    font-size: ${TYPOGRAPHY.caption.fontSize}px;
    line-height: ${TYPOGRAPHY.caption.lineHeight}px;
    font-variant-numeric: tabular-nums;
  }
  @keyframes redi-vertex-shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-3px); }
    75% { transform: translateX(3px); }
  }
  .${DEFENSE_PLAN_VERTEX_SHAKE_CLASS} {
    transform-box: fill-box;
    transform-origin: center;
    animation: redi-vertex-shake var(--redi-dur-base, 200ms) var(--redi-ease-standard, ease-out);
  }
  .${DEFENSE_PLAN_BEACON_HOVERED_CLASS} > div {
    transform: scale(1.1);
    transition: transform 120ms ease;
  }
  .${DEFENSE_PLAN_BEACON_SELECTED_CLASS} > div {
    transform: scale(1.18);
    transition: transform 120ms ease;
  }
`;

// Toggled by useDefensePlanLayer.web.ts on the map container while a plan is
// being viewed or the editor is open — see PLAN_FOCUS_CLASS below.
export const PLAN_FOCUS_CLASS = 'defense-plan-focused';

// Steps observation/structure pins back (opacity) and lets the plan's own
// pane paint over them, so a small feature doesn't hide under a pin sitting
// on top of it. Pins keep their pointer-events (unlike
// DEFENSE_PLAN_DRAWING_CLASS above) — they still take clicks, they just read
// second. The z-index override needs `!important`: ensureDefensePlanPane
// sets the pane's z-index as an inline style, which otherwise beats any
// stylesheet rule regardless of selector weight. The transition lives on the
// bare marker-pane rule (not the focused one) so it animates both the fade
// out and the fade back in.
export const PLAN_FOCUS_CSS = `
  .leaflet-marker-pane {
    transition: opacity 200ms ease;
  }
  .leaflet-container.${PLAN_FOCUS_CLASS} .leaflet-marker-pane {
    opacity: 0.35;
  }
  .leaflet-container.${PLAN_FOCUS_CLASS}.${DEFENSE_PLAN_HOME_CLASS} .leaflet-marker-pane {
    opacity: 1;
  }
  .leaflet-container.${PLAN_FOCUS_CLASS} .leaflet-defensePlan-pane {
    z-index: 650 !important;
  }
  /* Above the shape pane, not "...BeaconPane-pane": Leaflet's own
     createPane strips a literal "Pane" substring out of the name it's
     given before building the class (DomUtil's leaflet-<name>-pane, with
     name.replace('Pane', '')), so pane 'defensePlanBeaconPane' becomes
     class leaflet-defensePlanBeacon-pane, not leaflet-defensePlanBeaconPane-pane.
     Getting this wrong left the rule silently unmatched — the beacon pane
     stayed at its base z-index while the shape pane jumped to 650 above
     it, so a beacon centred inside its own area feature's fill lost every
     click to the polygon underneath it. */
  .leaflet-container.${PLAN_FOCUS_CLASS} .leaflet-defensePlanBeacon-pane {
    z-index: 660 !important;
  }
`;

// Tier 1 feel pass (Sept 2026): first-appearance + hover/selected treatment
// for marker icons. Targets an inner wrapper (`.redi-pin`, added inside each
// divIcon's `html`), never `.leaflet-marker-icon` itself — Leaflet positions
// that element with its own `transform: translate3d(...)`, and a second
// `transform` on the same element would replace it, not compose, silently
// teleporting the marker (same lesson as DEFENSE_PLAN_BEACON_SELECTED_CLASS
// above).
//
// `redi-pin--enter` (the entrance keyframe) is opt-in per icon build, not
// baked into `redi-pin` itself: useMarkerLayer.web.ts calls `setIcon()` on an
// existing marker to restyle it (selection, claim progress, badge changes,
// cluster counts) far more often than it creates a new one, and a keyframe
// animation replays every time the class is present on a freshly-inserted
// DOM node. Only the "new marker" call sites below pass `animate: true`;
// `setIcon()` restyles never do, so a restyle can never flicker.
export const MARKER_FEEL_CSS = `
  @keyframes redi-pin-in {
    from { opacity: 0; transform: scale(0.6); }
    to { opacity: 1; transform: scale(1); }
  }
  .redi-pin {
    cursor: pointer;
  }
  .redi-pin--enter {
    animation: redi-pin-in var(--redi-dur-base, 200ms) var(--redi-ease-standard, ease-out);
  }
  /* A plan point or beacon that was just drawn. An SVG path scales from its
     own centre only with transform-box: fill-box; the default origin is the
     top-left of the whole map's SVG. */
  .redi-plan-enter {
    transform-box: fill-box;
    transform-origin: center;
    animation: redi-pin-in var(--redi-dur-base, 200ms) var(--redi-ease-standard, ease-out);
  }
  .leaflet-marker-icon:hover .redi-pin {
    transform: scale(1.12);
    transition: transform var(--redi-dur-quick, 150ms) var(--redi-ease-standard, ease-out);
  }
  /* setIcon() swaps the pin's inner element, so a transition on the selected
     state has nothing to transition from; the keyframe plays on the fresh
     node instead and lands on the same resting state as the rule below. */
  @keyframes redi-pin-select {
    from { transform: scale(1); box-shadow: 0 0 0 0 transparent; }
    to { transform: scale(1.12); box-shadow: 0 0 0 2px var(--redi-focus-ring, #2e6b50); }
  }
  .redi-pin--selected {
    transform: scale(1.12);
    box-shadow: 0 0 0 2px var(--redi-focus-ring, #2e6b50);
    border-radius: 50%;
    animation: redi-pin-select var(--redi-dur-quick, 150ms) var(--redi-ease-standard, ease-out);
  }
  /* Observation dot whose post is open in the right dock. The dot's inline
     box-shadow (halo + drop shadow) would beat the ring above, so the ring
     is an outline here, and the keyframe animates only transform and
     outline-color rather than the box-shadow the base rule animates. The
     delta triangle is not round, so it gets the scale alone. */
  @keyframes redi-post-select {
    from { transform: scale(1); outline-color: transparent; }
    to { transform: scale(1.12); outline-color: var(--redi-focus-ring, #2e6b50); }
  }
  .post-marker .redi-pin--selected {
    box-shadow: none;
    outline: 2px solid var(--redi-focus-ring, #2e6b50);
    outline-offset: 2px;
    animation-name: redi-post-select;
  }
  .post-marker-delta .redi-pin--selected {
    outline: none;
  }
  @keyframes redi-arrive {
    from { opacity: 0.85; transform: scale(0.4); }
    to { opacity: 0; transform: scale(1.7); }
  }
  .redi-arrive {
    width: 44px;
    height: 44px;
    box-sizing: border-box;
    border-radius: 50%;
    border: 2px solid var(--redi-focus-ring, #2e6b50);
    pointer-events: none;
    animation: redi-arrive var(--redi-dur-slow, 320ms) var(--redi-ease-exit, ease-out) both;
  }
`;

const ARRIVE_RING_SIZE = 44;
const ARRIVE_RING_Z_OFFSET = -500;

/**
 * One-shot ring where a focus move landed, so the eye finds the house the
 * camera just brought in. Non-interactive, under the pin, and it removes
 * itself when the keyframe ends (or after a safety timeout if the animation
 * never runs). Skipped under reduced motion: the CSS vars collapse to 0ms
 * there, and a ring that flashes at 0ms is just noise.
 */
export function playArrivalRing(map: L.Map, at: L.LatLngExpression): void {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }
  const marker = getLeaflet()
    .marker(at, {
      interactive: false,
      keyboard: false,
      zIndexOffset: ARRIVE_RING_Z_OFFSET,
      icon: getLeaflet().divIcon({
        className: 'redi-arrive-host',
        html: '<div class="redi-arrive"></div>',
        iconSize: [ARRIVE_RING_SIZE, ARRIVE_RING_SIZE],
        iconAnchor: [ARRIVE_RING_SIZE / 2, ARRIVE_RING_SIZE / 2],
      }),
    })
    .addTo(map);
  let done = false;
  const remove = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    marker.remove();
  };
  const timer = setTimeout(remove, 1500);
  marker.getElement()?.addEventListener('animationend', remove, { once: true });
}

// Deferred require — Leaflet touches `window` at import time, which crashes
// SSR / Node-side rendering. The webpack import must be lazy so the module
// is only resolved in the browser.
export function getLeaflet(): typeof L {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  return require('leaflet');
}

export function injectLeafletStyles(): void {
  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  if (!document.getElementById('structure-label-css')) {
    const style = document.createElement('style');
    style.id = 'structure-label-css';
    style.innerHTML = STRUCTURE_LABEL_CSS;
    document.head.appendChild(style);
  }
  if (!document.getElementById('defense-plan-css')) {
    const style = document.createElement('style');
    style.id = 'defense-plan-css';
    style.innerHTML = DEFENSE_PLAN_CSS;
    document.head.appendChild(style);
  }
  if (!document.getElementById('defense-plan-focus-css')) {
    const style = document.createElement('style');
    style.id = 'defense-plan-focus-css';
    style.innerHTML = PLAN_FOCUS_CSS;
    document.head.appendChild(style);
  }
  if (!document.getElementById('marker-feel-css')) {
    const style = document.createElement('style');
    style.id = 'marker-feel-css';
    style.innerHTML = MARKER_FEEL_CSS;
    document.head.appendChild(style);
  }
}

/**
 * Leaflet's popup is white with dark text whatever the theme, which in dark
 * mode put a white card on a dark map. Re-colors the card, its tip and the
 * close button from the active theme. The popup's own markup (see
 * communityProgramPopup.ts) sets no colors, so its text follows along.
 * Scoped under .leaflet-container to outrank leaflet.css, whichever of the
 * two lands in <head> first. Rewritten on every theme change.
 */
export function applyLeafletPopupTheme(colors: {
  surfaceElevated: string;
  text: string;
  textSecondary: string;
  border: string;
}): void {
  const id = 'leaflet-popup-theme-css';
  let style = document.getElementById(id);
  if (!style) {
    style = document.createElement('style');
    style.id = id;
    document.head.appendChild(style);
  }
  style.innerHTML = `
  .leaflet-container .leaflet-popup-content-wrapper,
  .leaflet-container .leaflet-popup-tip {
    background: ${colors.surfaceElevated};
    color: ${colors.text};
  }
  .leaflet-container .leaflet-popup-content-wrapper {
    border: 1px solid ${colors.border};
  }
  .leaflet-container .leaflet-popup-content a {
    color: ${colors.text};
    text-decoration: underline;
  }
  .leaflet-container .${DEFENSE_PLAN_READOUT_CLASS} {
    background: ${colors.surfaceElevated};
    color: ${colors.text};
    border-color: ${colors.border};
  }
  .leaflet-container a.leaflet-popup-close-button {
    color: ${colors.textSecondary};
  }
  .leaflet-container a.leaflet-popup-close-button:hover,
  .leaflet-container a.leaflet-popup-close-button:focus {
    color: ${colors.text};
  }
`;
}

export function configureDefaultIcons(): void {
  const L = getLeaflet();
  delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: string })._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: '/leaflet/images/marker-icon-2x.png',
    iconUrl: '/leaflet/images/marker-icon.png',
    shadowUrl: '/leaflet/images/marker-shadow.png',
  });
}

/*
 * ---------------------------------------------------------------------------
 * Standard-basemap color treatment
 * ---------------------------------------------------------------------------
 *
 * The map used to be flatly near-monochrome (`saturate(0.12)`), from the
 * Design B mockup line "the map itself goes near-monochrome so category pins
 * are the only color". Glenn asked for the greens back on 2026-08-05: the
 * illustrated view read grey, and the wooded areas around Big Bear are most of
 * what the map is showing.
 *
 * The reason for the muting still stands, so this does not just turn the dial
 * up. It raises saturation only where the pixel is already green and leaves
 * every neutral surface alone, so woods and parks come back while roads,
 * building footprints and the residential fill stay calm and the readiness
 * pins keep the screen.
 *
 * CSS saturate() cannot target a hue, so this is an SVG filter mixing two
 * branches of the same tile:
 *
 *     calm  = saturate(CALM)                  the old near-monochrome look
 *     vivid = saturate(NATURE)                pushed
 *     mask  = G - (R + B) / 2, amplified      greenness; 0 for any neutral
 *     out   = mask * vivid + (1 - mask) * calm
 *
 * Greenness against OSM Carto's actual fills: forest #add19e 0.17, grass
 * #cdebb0 0.18, park #c8facc 0.19 (boosted hard); farmland #eef0d5 0.06
 * (slight lift); building #d9d0c9 and white road 0.00, motorway #e892a2
 * negative (untouched). Any grey scores exactly 0 because R = G = B cancels,
 * which is what keeps the built environment out of it.
 *
 * The mask survives dark mode's invert + 180deg hue-rotate: that pair is
 * identity for neutrals and turns forest green into a dark green of the same
 * greenness, so the same construction works on both, with its own constants.
 */

/** Saturation of the branch every non-green pixel ends up on. */
const CALM_SATURATION = 0.32;
const CALM_SATURATION_DARK = 0.18;
/**
 * Saturation of the branch green pixels are pulled toward. Picked off a
 * sweep over the Big Bear tiles against raw OSM as the reference: 1.25 barely
 * beats stock OSM, 1.85 flattens the national forest into one poster-green
 * shape.
 */
const NATURE_SATURATION = 1.5;
const NATURE_SATURATION_DARK = 1.0;
/**
 * Brightness the green branch is scaled to. Dark mode needs this and light
 * mode does not. At full brightness the national forest fills most of the
 * viewport at the zoom the map opens at, and a lit green sheet against
 * near-black chrome is the exact thing the dark treatment exists to avoid —
 * it also drowns the green readiness pins. 0.55 keeps the woods unmistakably
 * green while sitting them down in the dark.
 */
const NATURE_DIM = 1;
const NATURE_DIM_DARK = 0.55;
/** Turns a ~0.17 greenness into a nearly full mix of the vivid branch. */
const NATURE_MASK_GAIN = 5;

const FILTER_DEFS_ID = 'redi-map-filter-defs';
const NATURE_FILTER_ID = 'redi-basemap-nature';
const NATURE_FILTER_DARK_ID = 'redi-basemap-nature-dark';

function natureFilterMarkup(id: string, calm: number, nature: number, dim: number): string {
  // color-interpolation-filters="sRGB" is required. The linearRGB default
  // would shift every color before the mask ever ran.
  const dimNode =
    dim === 1
      ? ''
      : `<feColorMatrix in="vividSat" type="matrix" result="vivid"
        values="${dim} 0 0 0 0
                0 ${dim} 0 0 0
                0 0 ${dim} 0 0
                0 0 0 1 0"/>`;
  return `
    <filter id="${id}" color-interpolation-filters="sRGB">
      <feColorMatrix in="SourceGraphic" type="saturate" values="${calm}" result="calm"/>
      <feColorMatrix in="SourceGraphic" type="saturate" values="${nature}"
        result="${dim === 1 ? 'vivid' : 'vividSat'}"/>
      ${dimNode}
      <feColorMatrix in="SourceGraphic" type="matrix" result="greenness"
        values="0 0 0 0 0
                0 0 0 0 0
                0 0 0 0 0
                -0.5 1 -0.5 0 0"/>
      <feComponentTransfer in="greenness" result="mask">
        <feFuncA type="linear" slope="${NATURE_MASK_GAIN}" intercept="0"/>
      </feComponentTransfer>
      <feComposite in="vivid" in2="mask" operator="in" result="vividGreen"/>
      <feComposite in="vividGreen" in2="calm" operator="over"/>
    </filter>`;
}

/**
 * Put the filter definitions in the document, once.
 *
 * Not optional and not lazy: a CSS `filter: url(#missing)` does not fall back,
 * it stops the element rendering altogether. Without this the map goes blank
 * rather than grey, so it has to run before the filter string is assigned.
 */
export function ensureBasemapFilterDefs(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FILTER_DEFS_ID)) return;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('id', FILTER_DEFS_ID);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';
  svg.style.width = '0';
  svg.style.height = '0';
  svg.style.overflow = 'hidden';
  svg.innerHTML =
    natureFilterMarkup(NATURE_FILTER_ID, CALM_SATURATION, NATURE_SATURATION, NATURE_DIM) +
    natureFilterMarkup(
      NATURE_FILTER_DARK_ID,
      CALM_SATURATION_DARK,
      NATURE_SATURATION_DARK,
      NATURE_DIM_DARK
    );
  document.body.appendChild(svg);
}

/**
 * Applied to the tile layer's own container, never the map root — markers,
 * clusters and the hover card live in sibling panes and must keep their color.
 *
 * Dark inverts before the nature filter. Dimming alone leaves a pale grey
 * sheet glowing against near-black chrome; invert + a 180deg hue-rotate turns
 * OSM's light cartography into a dark one, keeps water cooler than land, and
 * flips the baked-in label text to light-on-dark.
 *
 * Shared so the main map and the location-picker mini-map can't drift —
 * they did, and a full-color mini-map inside a dark dialog was the tell.
 *
 * Satellite is the exception and gets almost none of this. Photography
 * carries its own color, touching it defeats the point of turning it on, and
 * inverting it produces nonsense. Dark mode only takes the glare off so a lit
 * rectangle doesn't sit inside near-black chrome. The default argument keeps
 * the location-picker mini-map, which has no style toggle, on the standard
 * treatment.
 */
export function applyBasemapFilter(
  layer: L.TileLayer | null,
  isDark: boolean,
  style: MapStyle = 'standard'
): void {
  const container = layer?.getContainer();
  if (!container) return;
  if (style === 'satellite') {
    container.style.filter = isDark ? 'brightness(0.82) contrast(1.04)' : 'none';
    return;
  }
  ensureBasemapFilterDefs();
  container.style.filter = isDark
    ? `invert(1) hue-rotate(180deg) url(#${NATURE_FILTER_DARK_ID}) brightness(0.92) contrast(0.88)`
    : `url(#${NATURE_FILTER_ID}) contrast(1.02)`;
}

// Structure-marker post badge, corner-anchored on the point icon. 'delta'
// draws the shared before/after triangle glyph (TRIANGLE_PATH, matches the
// native marker and post pins); 'dot' draws a small category-colored circle.
// Kept as a rendering-only shape here — the structureId -> badge mapping and
// category -> color choice live in useMarkerLayer.web.ts / structureBadges.ts.
export interface PointIconBadge {
  kind: 'delta' | 'dot';
  color: string;
}

/**
 * Property marker: a house silhouette (HOUSE_PATH) filled with the readiness
 * color, white-outlined, with the optional corner badge above it.
 *
 * Shape carries "this is a property" and color carries readiness — two
 * channels, one meaning each. Properties drew as colored circles until August
 * 2026, which made them indistinguishable from observation dots at a glance.
 *
 * `border` is the outline width in on-screen pixels. The path is authored in
 * a 24-unit viewBox, so it converts to user units by the same scale the SVG
 * is drawn at, keeping the outline a constant thickness whether the pin is at
 * its 24px resting size or its 40px selected size.
 */
/** Whether a newly-built icon should play its first-appearance animation
 *  (`animate`, only for a genuinely new marker — see MARKER_FEEL_CSS) and
 *  whether it should draw the selected-structure ring (`selected`). Both
 *  default to off so every existing call site keeps its current look. */
export interface PointIconFeelOptions {
  animate?: boolean;
  selected?: boolean;
}

function pinClassName(opts?: PointIconFeelOptions, extra?: string): string {
  return [
    'redi-pin',
    extra,
    opts?.animate ? 'redi-pin--enter' : null,
    opts?.selected ? 'redi-pin--selected' : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/** Marks or unmarks an observation dot as the post open in the dock, by
 *  toggling the same class a selected structure pin wears. A class toggle,
 *  not setIcon, so the dot's element survives and only two markers change
 *  when the docked post does. */
export function setPostMarkerDocked(marker: L.Marker | undefined, docked: boolean): void {
  marker?.getElement()?.querySelector('.redi-pin')?.classList.toggle('redi-pin--selected', docked);
}

export function makePointIcon(
  color: string,
  size: number,
  border: number,
  badge?: PointIconBadge,
  feel?: PointIconFeelOptions
): L.DivIcon {
  const badgeHtml = badge
    ? badge.kind === 'delta'
      ? `<svg width="10" height="10" viewBox="0 0 24 24" fill="${badge.color}" stroke="${ON_COLOR.fill}" stroke-width="2.5" stroke-linejoin="round" style="position:absolute;top:-3px;right:-3px;"><path d="${TRIANGLE_PATH}"/></svg>`
      : `<div style="position:absolute;top:-3px;right:-3px;width:10px;height:10px;border-radius:50%;background:${badge.color};border:1.5px solid ${ON_COLOR.fill};"></div>`
    : '';
  const strokeWidth = (border * 24) / size;
  const house = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="${ON_COLOR.fill}" stroke-width="${strokeWidth}" stroke-linejoin="round" style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));"><path d="${HOUSE_PATH}"/></svg>`;
  return getLeaflet().divIcon({
    className: 'structure-marker',
    html: `<div class="${pinClassName(feel)}" style="position:relative;overflow:visible;width:${size}px;height:${size}px;">${house}${badgeHtml}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export function makeClusterIcon(
  count: number,
  _abbreviated: string | number,
  feel?: PointIconFeelOptions
): L.DivIcon {
  // Property counts removed from the main map per client plan ("they
  // clutter the look"). Bubble size still scales with count so dense areas
  // visually pop, but the numeric label is suppressed.
  const size = propertyClusterSize(count);
  // House fills about 60% of the halo disc. Stroke width is in viewBox units.
  const glyph = Math.round(size * 0.6);
  const house = `<svg width="${glyph}" height="${glyph}" viewBox="0 0 24 24" fill="${PROPERTY_CLUSTER_FILL}" stroke="${ON_COLOR.fill}" stroke-width="1.5" stroke-linejoin="round" style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));"><path d="${HOUSE_PATH}"/></svg>`;
  return getLeaflet().divIcon({
    className: 'sc-cluster',
    html: `<div class="${pinClassName(feel)}" style="width:${size}px;height:${size}px;">${house}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Bubble standing for several observations. Same no-numeric-label rule as the
 * property clusters above ("they clutter the look") — size carries density.
 *
 * `tint` is the color of the category most of its members share, or undefined
 * when the mix has no clear majority. A cluster wearing its dominant category
 * keeps the map's color legible when zoomed out; a mixed one falls back to the
 * neutral slate rather than claiming a majority it doesn't have.
 */
export function makeObservationClusterIcon(
  count: number,
  tint?: string,
  feel?: PointIconFeelOptions
): L.DivIcon {
  const size = clusterBubbleSize(count);
  const inner = size - 10;
  // The two tinted layers mirror an observation dot's own fill + soft halo, so
  // a cluster reads as the same kind of thing as the dots it stands for.
  const halo = tint ? `background:${tint}38;` : '';
  const core = tint ? `background:${tint};` : '';
  return getLeaflet().divIcon({
    className: 'obs-cluster',
    html: `<div class="${pinClassName(feel, 'obs-halo')}" style="width:${size}px;height:${size}px;${halo}"><div class="obs-core" style="width:${inner}px;height:${inner}px;${core}"></div></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export function makeUserLocationIcon(color: string): L.DivIcon {
  return getLeaflet().divIcon({
    className: 'user-location-marker',
    html: `<div style="width: 16px; height: 16px; background: ${color}; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/**
 * Translucent ring around the user-location dot, sized to the fix's reported
 * accuracy in metres. It is what tells the user how much to trust the dot: a
 * GPS fix draws a ring too small to notice at street zoom, a wifi-derived
 * desktop fix draws an obviously wide one. `interactive: false` keeps it from
 * swallowing clicks meant for the pins underneath.
 */
export function makeAccuracyCircle(
  latlng: [number, number],
  radiusM: number,
  color: string
): L.Circle {
  return getLeaflet().circle(latlng, {
    radius: radiusM,
    color,
    weight: 1,
    opacity: 0.4,
    fillColor: color,
    fillOpacity: 0.12,
    interactive: false,
  });
}

// Approximate inverse of the latitude-delta -> zoom mapping used by the
// native MapView. Used when the screen first loads to pick a zoom level
// that matches a requested region's viewport size.
export function calculateZoom(latitudeDelta: number): number {
  return Math.max(0, Math.round(Math.log2(360 / latitudeDelta)));
}

export function showLocationError(error: GeolocationPositionError): void {
  let message: string;
  switch (error.code) {
    case error.PERMISSION_DENIED:
      message =
        'Location access denied. Please enable Location Services in your browser and system settings.';
      break;
    case error.POSITION_UNAVAILABLE:
      message =
        'Unable to determine your location. Please check that Location Services is enabled in System Settings.';
      break;
    case error.TIMEOUT:
      message =
        'Location request timed out. Please check that Location Services is enabled and try again.';
      break;
    default:
      message = 'Unable to get your location.';
  }
  window.alert(message);
}
